/**
 * Virtual shifting math (SPEC §6.3).
 *   v_virt = cad/60 · r_v · C,   v_phys = cad/60 · r_p · C
 *   F_road(v) = m·g·(sin θ_eff + Crr·cos θ_eff) + ½ρ·CdA_eff·v²,  θ_eff = road grade × difficulty
 *   F_tr = F_road(v_virt) · r_v / r_p,   P_req = F_road(v_virt) · v_virt
 * Identity: F_tr · v_phys = P_req (pedal power matches the virtual gear).
 */
import { airDensity, DEFAULT_CRR, G } from './constants';

export interface VsRoad {
  gradePct: number;
  difficultyPct: number;
  altitudeM: number;
  draft: number;
}

export interface VsRider {
  massKg: number;
  cda: number;
  crr?: number;
}

export function speedFromCadence(cadenceRpm: number, ratio: number, wheelM: number): number {
  return (cadenceRpm / 60) * ratio * wheelM;
}

export function roadForce(v: number, rider: VsRider, road: VsRoad): number {
  const theta = Math.atan((road.gradePct * road.difficultyPct) / 100 / 100);
  const crr = rider.crr ?? DEFAULT_CRR;
  const cdaEff = rider.cda * (1 - road.draft);
  return (
    rider.massKg * G * (Math.sin(theta) + crr * Math.cos(theta)) +
    0.5 * airDensity(road.altitudeM) * cdaEff * v * v
  );
}

export interface VsTargets {
  vVirt: number;
  vPhys: number;
  fRoad: number;
  fTrainer: number;
  pReq: number;
}

export function vsTargets(args: {
  cadenceRpm: number;
  virtualRatio: number;
  physicalRatio: number;
  wheelM: number;
  rider: VsRider;
  road: VsRoad;
  /** Measured flywheel speed (m/s) if trusted; otherwise derived from cadence. */
  vPhysMeasured?: number;
}): VsTargets {
  const vVirt = speedFromCadence(args.cadenceRpm, args.virtualRatio, args.wheelM);
  const vPhys = args.vPhysMeasured ?? speedFromCadence(args.cadenceRpm, args.physicalRatio, args.wheelM);
  const fRoad = roadForce(vVirt, args.rider, args.road);
  return {
    vVirt,
    vPhys,
    fRoad,
    fTrainer: (fRoad * args.virtualRatio) / args.physicalRatio,
    pReq: fRoad * vVirt,
  };
}

/** Trainer-reported speed is "plausible" if within ±25 % of the cadence-derived flywheel speed. */
export function plausibleTrainerSpeed(trainerKmh: number, vPhysFromCadence: number): number | undefined {
  const v = trainerKmh / 3.6;
  if (!(v > 1) || vPhysFromCadence < 1) return undefined;
  const r = v / vPhysFromCadence;
  return r > 0.75 && r < 1.25 ? v : undefined;
}

export const GRADE_MIN = -10;
export const GRADE_MAX = 20;

/**
 * Strategy A: grade the trainer must simulate so that its model a·sin γ + b + c·v² produces F at v.
 * (a = m_tr·g, b ≈ m_tr·g·crr_s + offset, c ≈ cw_s). Result in percent, clamped to −10 … +20 %.
 */
export function solveGrade(
  fTarget: number,
  vPhys: number,
  model: { a: number; b: number; c: number },
): number {
  const s = (fTarget - model.b - model.c * vPhys * vPhys) / model.a;
  const sMin = Math.sin(Math.atan(GRADE_MIN / 100));
  const sMax = Math.sin(Math.atan(GRADE_MAX / 100));
  const sc = Math.max(sMin, Math.min(sMax, s));
  return Math.tan(Math.asin(sc)) * 100;
}

/** Trainer's own force for a sent grade (used by tests / the inverse of solveGrade without offset). */
export function trainerModelForce(
  gradePct: number,
  vPhys: number,
  m: { a: number; b: number; c: number },
): number {
  return m.a * Math.sin(Math.atan(gradePct / 100)) + m.b + m.c * vPhys * vPhys;
}
